import {
  CalendarClock,
  FileText,
  HandCoins,
  User,
  Briefcase,
  Package,
  LayoutGrid,
  MessageSquare,
  Users,
  UsersRound,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  ShieldCheck,
  Headphones,
  X,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { memo, useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../rbac/PermissionsContext";
import { TOP_NAV_MODULE_KEYS } from "../../rbac/catalog";
import { getMaxDateInputValue } from "../../lib/attendanceDayChecks";
import RequestStatusDeskModal, { ALL_DESK_TYPES } from "./attendance/components/RequestStatusDeskModal";
import QuickRequestFormModal from "./attendance/components/QuickRequestFormModal";
import { attendanceStructureService } from "../../services/api";
import { mapLeaveRequestApiToInsight } from "../../lib/leaveApprovalWorkflow";
import { toast } from "react-hot-toast";

const RIGHT_SIDEBAR_WIDTH = 152;
const LEFT_SIDEBAR_WIDTH = 152;

const REQUEST_TYPE_DEFS = [
  { key: "regularization_checks", label: "Regularise checks" },
  { key: "leave_request", label: "Leave" },
  { key: "change_shift", label: "Change Shift" },
  { key: "resignation", label: "Resignation" },
  { key: "ticket_request", label: "Ticket" },
  { key: "tax_declaration", label: "Tax Declaration" },
  { key: "mrf_request", label: "MRF" },
  { key: "short_leave", label: "Short Leave" },
  { key: "announcements", label: "Announcements" },
];

const APPROVAL_ONLY_TYPE_DEFS = [
  { key: "onboarding_approval", label: "Employee Onboarding Approval" },
  { key: "transfer_approval", label: "Transfer Approval" },
  { key: "confirmation_approval", label: "Confirmation Approval" },
  { key: "payroll_lock_approval", label: "Payroll Lock Approval" },
  { key: "exit_separation_approval", label: "Exit / Separation Approval" },
];

// `module`     -> legacy company-plan flag (module_employee, module_attendance, ...)
// `rbacModule` -> RBAC module key from src/rbac/catalog.js (controls who sees the tab)
const navItems = [
  {
    name: "Configuration",
    to: "/admin/configuration",
    icon: FileText,
    module: "configuration",
    rbacModule: "configuration",
  },
  {
    name: "Employee",
    to: "/admin/employee",
    icon: Users,
    module: "employee",
    rbacModule: "employee",
  },
  {
    name: "Attendance",
    to: "/admin/attendance",
    icon: CalendarClock,
    module: "attendance",
    rbacModule: "attendance",
  },
  {
    name: "Payroll",
    to: "/admin/payroll",
    icon: HandCoins,
    module: "payroll",
    rbacModule: "payroll",
  },
  {
    name: "Hiring",
    to: "/admin/hiring",
    icon: Briefcase,
    module: "configuration",
    rbacModule: "hiring",
  },
  {
    name: "Assets",
    to: "/admin/assets",
    icon: Package,
    module: "configuration",
    rbacModule: "assets",
  },
  {
    name: "Communication",
    to: "/admin/communication",
    icon: MessageSquare,
    module: "configuration",
    rbacModule: "communication",
  },
  {
    name: "Helpdesk",
    to: "/admin/helpdesk",
    icon: Headphones,
    module: "configuration",
    rbacModule: "helpdesk",
  },
  {
    name: "User Roles",
    to: "/admin/user-role-management",
    icon: ShieldCheck,
    module: "configuration",
    rbacModule: "user_roles",
  },
];

const arraysMatch = (a = [], b = []) =>
  a.length === b.length && a.every((item, idx) => item === b[idx]);

const Sidebar = memo(function Sidebar({ setCurrentPage, darkMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyModuleAccess, hasScreenAccess, isAdmin, snapshot } = usePermissions();
  const [companyData, setCompanyData] = useState(null);
  const [rightOpen, setRightOpen] = useState(() => {
    const v = localStorage.getItem("adminRightSidebarOpen");
    return v == null ? true : v === "true";
  });
  const [leftOpen, setLeftOpen] = useState(() => {
    const v = localStorage.getItem("adminLeftSidebarOpen");
    return v == null ? false : v === "true";
  });
  const [expandedPanels, setExpandedPanels] = useState({
    usefulLinks: false,
  });
  const [selectedTypeByPanel, setSelectedTypeByPanel] = useState({
    review: ALL_DESK_TYPES,
    approvals: ALL_DESK_TYPES,
  });
  const [statusModal, setStatusModal] = useState({
    open: false,
    panelKey: "review",
    status: "Pending",
  });
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [quickActionRequests, setQuickActionRequests] = useState(() => {
    try {
      const raw = localStorage.getItem("adminQuickActionRequests");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [selectedStatusByPanel, setSelectedStatusByPanel] = useState({
    review: "Pending",
    approvals: "Pending",
  });
  const [statusDeskShowForm, setStatusDeskShowForm] = useState(false);
  const [statusDeskContext, setStatusDeskContext] = useState(null);
  const [statusActionModal, setStatusActionModal] = useState({
    open: false,
    requestIds: [],
    nextStatus: "",
    reason: "",
  });
  const [quickActionDraft, setQuickActionDraft] = useState({
    date: "",
    fromDate: "",
    toDate: "",
    shift: "",
    currentShift: "",
    leaveType: "CL",
    leaveDuration: "full_day",
    inTime: "",
    outTime: "",
    startTime: "",
    endTime: "",
    resignationReason: "",
    resignationDate: "",
    lastWorkingDate: "",
    reason: "",
    notes: "",
    ticketType: "",
    ticketPriority: "Medium",
    ticketSubject: "",
    amount: "",
    investmentSection: "",
    financialYear: "",
    taxRegime: "new",
    positionTitle: "",
    positionsCount: "1",
    requiredByDate: "",
    requestDepartment: "",
    requestDesignation: "",
  });
  const [usefulLinksAudience, setUsefulLinksAudience] = useState(() => {
    const v = localStorage.getItem("adminUsefulLinksAudience");
    return v === "employer" ? "employer" : "employee";
  });
  const maxDateToday = useMemo(() => getMaxDateInputValue(), []);

  useEffect(() => {
    const navRbacModules = navItems.map((item) => item.rbacModule);
    if (!arraysMatch(navRbacModules, TOP_NAV_MODULE_KEYS)) {
      console.warn("[RBAC] Sidebar navItems do not match catalog top-nav modules.", {
        sidebar: navRbacModules,
        catalog: TOP_NAV_MODULE_KEYS,
      });
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("adminUsefulLinksAudience", usefulLinksAudience);
  }, [usefulLinksAudience]);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("companyData"));
    setCompanyData(storedData);

    const handleCompanyUpdate = (e) => setCompanyData(e.detail);
    const onStorage = (e) => {
      if (e.key === "companyData") {
        try {
          setCompanyData(e.newValue ? JSON.parse(e.newValue) : null);
        } catch {
          setCompanyData(null);
        }
      }
    };
    window.addEventListener("companyDataUpdated", handleCompanyUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("companyDataUpdated", handleCompanyUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("adminRightSidebarOpen", String(rightOpen));
  }, [rightOpen]);
  useEffect(() => {
    localStorage.setItem("adminLeftSidebarOpen", String(leftOpen));
  }, [leftOpen]);

  useEffect(() => {
    const root = document.documentElement;
    if (rightOpen) {
      root.style.setProperty("--admin-right-sidebar-width", `${RIGHT_SIDEBAR_WIDTH}px`);
    } else {
      root.style.setProperty("--admin-right-sidebar-width", "0px");
    }
    if (leftOpen) {
      root.style.setProperty("--admin-left-sidebar-width", `${LEFT_SIDEBAR_WIDTH}px`);
    } else {
      root.style.setProperty("--admin-left-sidebar-width", "0px");
    }
    return () => {
      root.style.setProperty("--admin-right-sidebar-width", "0px");
      root.style.setProperty("--admin-left-sidebar-width", "0px");
    };
  }, [rightOpen, leftOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && (rightOpen || leftOpen)) {
        setRightOpen(false);
        setLeftOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [rightOpen, leftOpen]);

  const getModuleStatus = (moduleName) => {
    if (!companyData) return { isEnabled: true };
    let isEnabled = true;
    switch (moduleName) {
      case "employee":
        isEnabled = Number(companyData.module_employee) !== 0;
        break;
      case "attendance":
        isEnabled = Number(companyData.module_attendance) !== 0;
        break;
      case "payroll":
        isEnabled = Number(companyData.module_payroll) !== 0;
        break;
      case "reports":
        isEnabled = Number(companyData.module_reports) !== 0;
        break;
      default:
        isEnabled = true;
    }
    return { isEnabled };
  };

  const handleItemClick = (e, item) => {
    const { isEnabled } = getModuleStatus(item.module);
    if (!isEnabled) {
      e.preventDefault();
      alert(`The ${item.name} module is not enabled for your company plan.`);
      return;
    }
    const targetPath = item.to;
    if (item.name === "Employee" && location.pathname === targetPath) {
      window.dispatchEvent(new CustomEvent("closeEmployeeForm"));
    }
    setCurrentPage(item.name === "Attendance" ? "Time & Attendance" : item.name);
  };

  const getQuickPanelUserIds = () => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
    return {
      userId: currentUser?.id || null,
      employeeId: Number(localStorage.getItem("userId")) || null,
    };
  };

  const isOwnQuickRequest = (request, userId, employeeId) => {
    if (userId && request.userId === userId) return true;
    if (employeeId && Number(request.employeeId) === Number(employeeId)) return true;
    return false;
  };

  const panelBadgeCounts = useMemo(() => {
    const { userId, employeeId } = getQuickPanelUserIds();
    const requestTotal = quickActionRequests.filter(
      (request) =>
        request.panelKey === "review" && isOwnQuickRequest(request, userId, employeeId)
    ).length;
    const approvalTotal = quickActionRequests.filter(
      (request) =>
        request.status === "Pending" && !isOwnQuickRequest(request, userId, employeeId)
    ).length;
    return { review: requestTotal, approvals: approvalTotal };
  }, [quickActionRequests]);

  useEffect(() => {
    try {
      localStorage.setItem("admin_review_requests_count", String(panelBadgeCounts.review));
      localStorage.setItem("admin_approvals_count", String(panelBadgeCounts.approvals));
    } catch {
      // ignore storage failures
    }
  }, [panelBadgeCounts]);

  const openStatusModal = (panelKey, status) => {
    setSelectedStatusByPanel((prev) => ({
      ...prev,
      [panelKey]: status,
    }));
    setSelectedTypeByPanel((prev) => ({
      ...prev,
      [panelKey]: prev[panelKey] || ALL_DESK_TYPES,
    }));
    setStatusDeskShowForm(false);
    setStatusDeskContext(null);
    setSelectedRequestId(null);
    setStatusModal({
      open: true,
      panelKey,
      status,
    });
  };
  const getRequestsForStatus = (panelKey, statusValue, selectedType) => {
    const { userId, employeeId } = getQuickPanelUserIds();
    const approvalOnlyLabels = new Set(APPROVAL_ONLY_TYPE_DEFS.map((item) => item.label));
    const isAllTypes = !selectedType || selectedType === ALL_DESK_TYPES;

    return quickActionRequests
      .filter((request) => {
        if (isAllTypes) {
          if (panelKey === "approvals") return true;
          return request.panelKey === "review";
        }
        if (panelKey === "approvals") {
          if (approvalOnlyLabels.has(selectedType)) {
            return request.panelKey === "approvals" && request.actionLabel === selectedType;
          }
          return request.actionLabel === selectedType;
        }
        return request.panelKey === "review" && request.actionLabel === selectedType;
      })
      .filter((request) => request.status === statusValue)
      .filter((request) => {
        if (panelKey === "approvals") {
          return !isOwnQuickRequest(request, userId, employeeId);
        }
        return isOwnQuickRequest(request, userId, employeeId);
      });
  };
  const getFilteredRequests = () => {
    return getRequestsForStatus(
      statusModal.panelKey,
      selectedStatusByPanel[statusModal.panelKey] || statusModal.status || "Pending",
      selectedTypeByPanel[statusModal.panelKey] || ""
    );
  };
  useEffect(() => {
    if (!statusModal.open) return;
    const filtered = getFilteredRequests();
    if (filtered.length === 0) {
      setSelectedRequestId(null);
      return;
    }
    if (!filtered.some((request) => request.requestId === selectedRequestId)) {
      setSelectedRequestId(filtered[0].requestId);
    }
  }, [
    statusModal.open,
    statusModal.panelKey,
    statusModal.status,
    selectedStatusByPanel,
    selectedTypeByPanel,
    quickActionRequests,
    selectedRequestId,
  ]);
  const closeStatusModal = () => {
    setStatusModal((prev) => ({
      ...prev,
      open: false,
    }));
    setSelectedRequestId(null);
    setStatusDeskShowForm(false);
    setStatusDeskContext(null);
  };
  const applyStatusSelection = (status) => {
    setSelectedStatusByPanel((prev) => ({
      ...prev,
      [statusModal.panelKey]: status,
    }));
    const payload = {
      panelKey: statusModal.panelKey,
      status,
      selectedType: selectedTypeByPanel[statusModal.panelKey] || "",
      selectedAt: Date.now(),
      source: "sidebar_quick_status_modal",
    };
    try {
      localStorage.setItem("adminQuickActionStatus", JSON.stringify(payload));
    } catch {
      // ignore storage failures
    }
    window.dispatchEvent(new CustomEvent("admin:quick-action-status", { detail: payload }));
  };
  const updateQuickRequestStatus = (requestId, nextStatus, actionReason = "") => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
    const actorName = currentUser?.name || "Approver";
    setQuickActionRequests((prev) => {
      const updated = prev.map((request) =>
        request.requestId === requestId
          ? {
              ...request,
              status: nextStatus,
              updatedAt: Date.now(),
              pendingWith:
                nextStatus === "Pending"
                  ? request.pendingWith || "Reporting Manager"
                  : null,
              workflowTrail: [
                ...(Array.isArray(request.workflowTrail) ? request.workflowTrail : []),
                {
                  status: nextStatus,
                  actor: actorName,
                  actedAt: Date.now(),
                  reason: actionReason,
                },
              ],
            }
          : request
      );
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(updated));
      } catch {
        // ignore storage failures
      }
      return updated;
    });
  };

  const updateQuickRequestStatusBulk = (requestIds, nextStatus, actionReason = "") => {
    const idSet = new Set(requestIds);
    const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
    const actorName = currentUser?.name || "Approver";
    setQuickActionRequests((prev) => {
      const updated = prev.map((request) =>
        idSet.has(request.requestId)
          ? {
              ...request,
              status: nextStatus,
              updatedAt: Date.now(),
              pendingWith:
                nextStatus === "Pending"
                  ? request.pendingWith || "Reporting Manager"
                  : null,
              workflowTrail: [
                ...(Array.isArray(request.workflowTrail) ? request.workflowTrail : []),
                {
                  status: nextStatus,
                  actor: actorName,
                  actedAt: Date.now(),
                  reason: actionReason,
                },
              ],
            }
          : request
      );
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(updated));
      } catch {
        // ignore storage failures
      }
      return updated;
    });
  };
  const openStatusActionModal = (requestId, nextStatus) => {
    setStatusActionModal({
      open: true,
      requestIds: [requestId],
      nextStatus,
      reason: "",
    });
  };

  const openBulkStatusActionModal = (requestIds, nextStatus) => {
    if (!Array.isArray(requestIds) || requestIds.length === 0) return;
    setStatusActionModal({
      open: true,
      requestIds,
      nextStatus,
      reason: "",
    });
  };
  const closeStatusActionModal = () => {
    setStatusActionModal({
      open: false,
      requestIds: [],
      nextStatus: "",
      reason: "",
    });
  };
  const confirmStatusAction = () => {
    const reason = (statusActionModal.reason || "").trim();
    if (!reason || reason.length > 250) return;
    if (statusActionModal.requestIds.length === 1) {
      updateQuickRequestStatus(statusActionModal.requestIds[0], statusActionModal.nextStatus, reason);
    } else {
      updateQuickRequestStatusBulk(
        statusActionModal.requestIds,
        statusActionModal.nextStatus,
        reason
      );
    }
    closeStatusActionModal();
  };

  const resetQuickActionDraft = (actionLabel = "") => {
    const todayIso = getMaxDateInputValue();
    setQuickActionDraft({
      date: actionLabel === "Regularise checks" ? todayIso : "",
      fromDate: "",
      toDate: "",
      shift: "",
      currentShift: "",
      leaveType: "CL",
      leaveDuration: "full_day",
      inTime: "",
      outTime: "",
      startTime: "",
      endTime: "",
      resignationReason: "",
      resignationDate: "",
      lastWorkingDate: "",
      reason: "",
      notes: "",
      ticketType: "",
      ticketPriority: "Medium",
      ticketSubject: "",
      amount: "",
      investmentSection: "",
      financialYear: "",
      taxRegime: "new",
      positionTitle: "",
      positionsCount: "1",
      requiredByDate: "",
      requestDepartment: "",
      requestDesignation: "",
    });
  };

  const buildQuickActionContext = (actionLabel) => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
    const employeeId =
      currentUser?.employeeId ??
      currentUser?.employee?.id ??
      localStorage.getItem("hrms_current_employee_id") ??
      null;
    return {
      actionLabel,
      employeeId: employeeId != null ? String(employeeId) : null,
      userId: currentUser?.id || null,
      userName: currentUser?.name || "",
      userEmail: currentUser?.email || currentUser?.username || "",
      requestedAt: Date.now(),
    };
  };

  const openStatusDeskForm = () => {
    const selectedType = selectedTypeByPanel[statusModal.panelKey] || "";
    if (!selectedType || selectedType === "Announcements") return;
    const context = buildQuickActionContext(selectedType);
    resetQuickActionDraft(selectedType);
    setStatusDeskContext(context);
    setStatusDeskShowForm(true);
    setSelectedRequestId(null);
  };

  const closeStatusDeskForm = () => {
    setStatusDeskShowForm(false);
  };

  const submitRegulariseQuickAction = (formData) => {
    const context = statusDeskContext;
    const payload = {
      ...context,
      formData,
      submittedAt: Date.now(),
      source: "sidebar_status_desk",
    };
    try {
      localStorage.setItem("adminQuickActionDraft", JSON.stringify(payload));
    } catch {
      /* ignore */
    }
    const requestEntry = {
      requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      panelKey: "review",
      actionLabel: "Regularise checks",
      employeeId: context?.employeeId || null,
      userId: context?.userId || null,
      userName: context?.userName || "Current user",
      userEmail: context?.userEmail || "",
      status: "Pending",
      submittedAt: Date.now(),
      formData,
      pendingWith: "Reporting Manager",
      workflowTrail: [
        {
          status: "Pending",
          actor: context?.userName || "Current user",
          actedAt: Date.now(),
        },
      ],
    };
    setQuickActionRequests((prev) => {
      const next = [requestEntry, ...prev];
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    window.dispatchEvent(
      new CustomEvent("admin:quick-action-submitted", {
        detail: {
          ...payload,
          requestId: requestEntry.requestId,
          actionLabel: requestEntry.actionLabel,
        },
      })
    );
    closeStatusDeskForm();
    setSelectedRequestId(requestEntry.requestId);
    toast.success("Regularisation request submitted");
  };

  const submitQuickAction = async (e) => {
    e.preventDefault();
    const context = statusDeskContext;
    const actionLabel =
      context?.actionLabel === "Tax Declaration" ? "Investment Declaration" : context?.actionLabel;
    const payload = {
      ...context,
      formData: quickActionDraft,
      submittedAt: Date.now(),
      source: "sidebar_status_desk",
    };
    try {
      localStorage.setItem("adminQuickActionDraft", JSON.stringify(payload));
    } catch {
      // ignore storage failures
    }

    let requestEntry = {
      requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      panelKey: "review",
      actionLabel: context?.actionLabel || actionLabel,
      employeeId: context?.employeeId || null,
      userId: context?.userId || null,
      userName: context?.userName || "Current user",
      userEmail: context?.userEmail || "",
      status: "Pending",
      submittedAt: Date.now(),
      formData: quickActionDraft,
      pendingWith: "Reporting Manager",
      workflowTrail: [
        {
          status: "Pending",
          actor: context?.userName || "Current user",
          actedAt: Date.now(),
        },
      ],
    };

    if (actionLabel === "Leave" && context?.employeeId) {
      try {
        const fd = quickActionDraft || {};
        const apiRes = await attendanceStructureService.createLeaveRequest({
          employeeId: Number(context.employeeId),
          leaveStatusCode: fd.leaveType,
          leaveDuration: fd.leaveDuration || "full_day",
          fromDate: fd.fromDate,
          toDate: fd.toDate || fd.fromDate,
          reason: fd.reason,
          notes: fd.notes,
        });
        if (apiRes?.data) {
          const mapped = mapLeaveRequestApiToInsight(apiRes.data);
          requestEntry = {
            ...requestEntry,
            requestId: mapped.requestId,
            status: mapped.status,
            pendingWith: mapped.pendingAt,
            formData: mapped.formData,
          };
          if (apiRes.autoApproved) {
            toast.success("Leave auto-approved and applied");
          } else {
            toast.success(`Leave submitted — pending at ${mapped.pendingAt}`);
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to submit leave request");
        return;
      }
    }

    setQuickActionRequests((prev) => {
      const next = [requestEntry, ...prev];
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
    window.dispatchEvent(
      new CustomEvent("admin:quick-action-submitted", {
        detail: { ...payload, requestId: requestEntry.requestId, actionLabel: requestEntry.actionLabel },
      })
    );
    closeStatusDeskForm();
    setSelectedRequestId(requestEntry.requestId);
    toast.success("Request submitted");
  };

  const handleUsefulLinkClick = (link) => {
    if (!link?.href) return;
    if (link.key === "epf_establishment") {
      const pfCode =
        companyData?.pfEstablishmentCode ||
        companyData?.pf_code ||
        companyData?.pfCode ||
        "";
      const pfUsername = pfCode;
      const info = [
        "Opening EPF Establishment Portal.",
        pfCode
          ? `PF Establishment Code: ${pfCode}`
          : "PF Establishment Code: Not available",
        pfUsername
          ? `Username (auto): ${pfUsername}`
          : "Username: Not available",
      ].join("\n");
      if (pfUsername && navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(pfUsername).catch(() => {});
      }
      alert(info);
    }
    window.open(link.href, "_blank", "noopener,noreferrer");
  };

  const rightItems = useMemo(() => {
    return [
      {
        key: "review",
        label: "Request",
        icon: ClipboardCheck,
        badge: panelBadgeCounts.review,
      },
      {
        key: "approvals",
        label: "Approvals",
        icon: ShieldCheck,
        badge: panelBadgeCounts.approvals,
      },
    ];
  }, [panelBadgeCounts]);

  const panelTypeItemsByKey = useMemo(() => {
    const allowQuickPanel = (screenKey) =>
      isAdmin || hasScreenAccess("ess_quick_panel", screenKey, "view");
    const requestTypes = REQUEST_TYPE_DEFS.filter((item) => allowQuickPanel(item.key)).map(
      (item) => item.label
    );
    return {
      review: requestTypes,
      approvals: [
        ...requestTypes,
        ...APPROVAL_ONLY_TYPE_DEFS.map((item) => item.label),
      ],
    };
  }, [hasScreenAccess, isAdmin, snapshot]);

  const usefulLinks = useMemo(
    () => ({
      employee: [
        { key: "pf_member", label: "PF Member Portal", href: "https://unifiedportal-mem.epfindia.gov.in/memberinterface/" },
        { key: "esi_member", label: "ESI Member Portal", href: "https://www.esic.in/EmployeePortal/login.aspx" },
        { key: "pf_passbook", label: "PF Passbook", href: "https://passbook.epfindia.gov.in/MemberPassBook/Login" },
        { key: "it_login", label: "Income Tax Login", href: "https://www.incometax.gov.in/iec/foportal/" },
      ],
      employer: [
        { key: "epf_establishment", label: "EPF Establishment Login", href: "https://unifiedportal-emp.epfindia.gov.in/epfo/" },
        { key: "esi_establishment", label: "ESI Establishment Login", href: "https://portal.esic.gov.in/EmployerPortal/ESICInsurancePortal/Portal_Loginnew.aspx" },
        { key: "pt_login", label: "PT Login", href: "https://pt.kar.nic.in/" },
        { key: "ekarmika", label: "eKarmika Login", href: "https://www.ekarmika.karnataka.gov.in/ekarmika/static/home.aspx" },
        { key: "lwf_login", label: "LWF Login", href: "https://klwbapps.karnataka.gov.in/lowf/" },
      ],
    }),
    []
  );

  const leftItems = useMemo(
    () => [
      {
        key: "home",
        label: "Home",
        icon: LayoutGrid,
        to: "/admin/home",
      },
      {
        key: "profile",
        label: "Profile",
        icon: User,
        to: "/employee/profile",
      },
      {
        key: "attendance",
        label: "Attendance",
        icon: CalendarClock,
        to: "/employee/attendance",
      },
      {
        key: "payroll",
        label: "Payroll",
        icon: HandCoins,
        to: "/employee/payroll",
      },
      {
        key: "reports",
        label: "Reports",
        icon: FileText,
        to: "/employee/reports",
      },
    ],
    []
  );

  const teamItems = useMemo(
    () => [
      {
        key: "my_team",
        label: "My Team",
        icon: UsersRound,
        to: "/employee/my-team",
      },
    ],
    []
  );

  return (
    <>
      <div className="sticky top-0 z-[85] -mt-1 mb-1 bg-white font-sans antialiased shadow-sm dark:bg-[#121212]">
        <div className="pl-2 pr-4 py-1.5 w-full">
          <div className="flex w-full min-w-0 items-center justify-start gap-[2.4px] overflow-x-auto scrollbar-hide">
            {navItems
              .filter((item) => isAdmin || hasAnyModuleAccess(item.rbacModule))
              .map((item) => {
              const active = location.pathname === item.to;
              const { isEnabled } = getModuleStatus(item.module);
              return (
                <Link
                  key={item.name}
                  to={item.to}
                  onClick={(e) => handleItemClick(e, item)}
                  className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap min-w-[88px] px-1.5 py-2 border-b-2 text-sm font-semibold transition ${
                    active
                      ? "border-redhalo-red text-black dark:border-redhalo-red dark:text-black"
                      : isEnabled
                      ? "border-transparent text-black hover:text-black dark:text-black dark:hover:text-black"
                      : "text-gray-400 border-transparent opacity-70 cursor-not-allowed"
                  }`}
                >
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {!leftOpen && (
        <div className="group fixed left-0 top-1/2 z-[60] -translate-y-1/2">
          <button
            type="button"
            onClick={() => setLeftOpen(true)}
            className="flex h-14 w-6 items-center justify-center rounded-r-full border border-l-0 border-gray-300/70 dark:border-[#3C3C4C]/70 bg-white/95 dark:bg-[#1D1D1D]/95 shadow-lg backdrop-blur-md transition hover:w-7 hover:bg-white dark:hover:bg-[#2a2a2a]"
            aria-label="Open Employee Panel"
          >
            <ChevronRight
              size={14}
              className="text-redhalo-red dark:text-redhalo-mint"
            />
          </button>
          <span className="pointer-events-none absolute left-full top-1/2 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-200 group-hover:block group-focus-within:block">
            Open Employee Panel
          </span>
        </div>
      )}

      <div
        className={`fixed top-20 left-0 z-50 h-[calc(100dvh-5rem)] transition-transform duration-300 ease-out ${
          leftOpen ? "translate-x-0" : "-translate-x-full pointer-events-none"
        }`}
        style={{ width: LEFT_SIDEBAR_WIDTH }}
      >
        {leftOpen && (
          <button
            type="button"
            onClick={() => setLeftOpen(false)}
            className="absolute right-0 top-1/2 z-[61] translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-300/70 dark:border-[#3C3C4C]/70 bg-white/95 dark:bg-[#1D1D1D]/95 p-2 shadow-md backdrop-blur-md transition hover:bg-white dark:hover:bg-[#2a2a2a]"
            title="Close Employee Panel"
            aria-label="Close Employee Panel"
          >
            <ChevronLeft
              size={14}
              className="text-redhalo-red dark:text-redhalo-mint"
            />
          </button>
        )}
        <aside
          className="h-full w-80 overflow-hidden rounded-r-md bg-white dark:bg-[#121212] shadow-xl text-black dark:text-gray-100"
          style={{ width: LEFT_SIDEBAR_WIDTH }}
        >
          <div className="flex h-full flex-col overflow-hidden rounded-r-md">
            <div className="flex items-center justify-between rounded-tr-md bg-white px-4 py-3 dark:bg-[#121212]">
              <div className="flex items-center gap-2 p-1.5">
                <Users size={16} className="text-redhalo-red dark:text-redhalo-mint" />
                <h2 className="text-sm font-semibold text-redhalo-red dark:text-redhalo-mint">
                  My Section
                </h2>
              </div>
            </div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              {leftItems.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      navigate(item.to);
                      setCurrentPage(item.label);
                    }}
                    className={`flex w-full items-center gap-3 px-3 py-3 text-left transition ${
                      isActive
                        ? "border-l-[3px] border-redhalo-red bg-white dark:border-redhalo-red dark:bg-[#121212]"
                        : "border-l-[3px] border-transparent bg-white hover:bg-gray-50 dark:border-transparent dark:bg-[#121212] dark:hover:bg-[#1a1a1a]"
                    }`}
                  >
                    <item.icon
                      size={18}
                      className={
                        isActive
                          ? "text-redhalo-red dark:text-redhalo-mint"
                          : "text-gray-700 dark:text-gray-300"
                      }
                    />
                    <span className="text-sm font-medium text-black dark:text-gray-100">
                      {item.label}
                    </span>
                  </button>
                );
              })}

              <div className="pt-2">

                {teamItems.map((item) => {
                  const isActive = location.pathname === item.to;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        navigate(item.to);
                        setCurrentPage(item.label);
                      }}
                      className={`flex w-full items-center gap-3 px-3 py-3 text-left transition ${
                        isActive
                          ? "border-l-[3px] border-redhalo-red bg-white dark:border-redhalo-red dark:bg-[#121212]"
                          : "border-l-[3px] border-transparent bg-white hover:bg-gray-50 dark:border-transparent dark:bg-[#121212] dark:hover:bg-[#1a1a1a]"
                      }`}
                      title="View employees who report to you"
                    >
                      <item.icon
                        size={18}
                        className={
                          isActive
                            ? "text-redhalo-red dark:text-redhalo-mint"
                            : "text-gray-700 dark:text-gray-300"
                        }
                      />
                      <span className="text-sm font-medium text-black dark:text-gray-100">
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {!rightOpen && (
        <div className="group fixed right-0 top-1/2 z-[60] -translate-y-1/2">
          <button
            type="button"
            onClick={() => setRightOpen(true)}
            className="flex h-14 w-6 items-center justify-center rounded-l-full border border-r-0 border-gray-300/70 dark:border-[#3C3C4C]/70 bg-white/95 dark:bg-[#1D1D1D]/95 shadow-lg backdrop-blur-md transition hover:w-7 hover:bg-white dark:hover:bg-[#2a2a2a]"
            aria-label="Open Quick Panel"
          >
            <ChevronLeft
              size={14}
              className="text-redhalo-red dark:text-redhalo-mint"
            />
          </button>
          <span className="pointer-events-none absolute right-full top-1/2 mr-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-200 group-hover:block group-focus-within:block">
            Open Quick Panel
          </span>
        </div>
      )}

      <div
        className={`fixed top-20 right-0 z-50 h-[calc(100dvh-5rem)] transition-transform duration-300 ease-out ${
          rightOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
        style={{ width: RIGHT_SIDEBAR_WIDTH }}
      >
        {rightOpen && (
          <button
            type="button"
            onClick={() => setRightOpen(false)}
            className="absolute left-0 top-1/2 z-[61] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-300/70 dark:border-[#3C3C4C]/70 bg-white/95 dark:bg-[#1D1D1D]/95 p-2 shadow-md backdrop-blur-md transition hover:bg-white dark:hover:bg-[#2a2a2a]"
            title="Close Quick Panel"
            aria-label="Close Quick Panel"
          >
            <ChevronRight
              size={14}
              className="text-redhalo-red dark:text-redhalo-mint"
            />
          </button>
        )}

        <aside
          className="h-full w-80 overflow-hidden rounded-l-md bg-white dark:bg-[#121212] shadow-xl text-black dark:text-gray-100"
          style={{ width: RIGHT_SIDEBAR_WIDTH }}
        >
          <div className="flex h-full flex-col overflow-hidden rounded-l-md">
            <div className="flex items-center justify-between rounded-tl-md border-b border-gray-200 bg-white px-4 py-3 dark:border-[#3C3C4C] dark:bg-[#121212]">
              <div className="flex items-center gap-2 p-1.5">
                <LayoutGrid size={16} className="text-redhalo-red dark:text-redhalo-mint" />
                <h2 className="text-sm font-semibold text-redhalo-red dark:text-redhalo-mint">Quick Panel</h2>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              {rightItems.map((item) => {
                const isActive =
                  statusModal.open && statusModal.panelKey === item.key;
                return (
                  <div
                    key={item.key}
                    className={`overflow-hidden border-l-[3px] ${
                      isActive
                        ? "border-l-redhalo-red bg-white dark:border-l-redhalo-red dark:bg-[#121212]"
                        : "border-l-transparent bg-white dark:border-l-transparent dark:bg-[#121212]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        openStatusModal(
                          item.key,
                          selectedStatusByPanel[item.key] || "Pending"
                        )
                      }
                      className="flex w-full items-center justify-between px-3 py-2.5 transition hover:bg-gray-50/80 dark:hover:bg-[#1a1a1a]"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-black dark:text-gray-100">
                        <item.icon
                          size={16}
                          className={
                            isActive
                              ? "text-redhalo-red dark:text-redhalo-mint"
                              : "text-gray-700 dark:text-gray-300"
                          }
                        />
                        {item.label}
                      </span>
                      <span
                        className={`min-w-[18px] rounded-full px-1.5 h-5 flex items-center justify-center text-xs font-semibold ${
                          item.badge > 0
                            ? "bg-redhalo-red text-white"
                            : "bg-gray-200 dark:bg-[#2a2a2a] text-gray-600 dark:text-gray-300"
                        }`}
                      >
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Useful Links - bottom anchored, expands upward */}
            <div className="mt-auto p-3 border-t border-gray-200 dark:border-[#3C3C4C] relative">
              <div className="relative">
                {expandedPanels.usefulLinks && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 rounded-lg border border-gray-200 dark:border-[#3C3C4C] border-l-[3px] border-l-redhalo-red bg-gradient-to-b from-redhalo-mint/90 to-gray-panel dark:from-[#1D1D1D] dark:to-[#121212] max-h-72 overflow-y-auto shadow-panel">
                    <div className="p-2 space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        {(usefulLinksAudience === "employee"
                          ? usefulLinks.employee
                          : usefulLinks.employer
                        ).map((link) => (
                          <button
                            key={link.key}
                            type="button"
                            onClick={() => handleUsefulLinkClick(link)}
                            className={`text-[11px] px-2 py-1 rounded-full border transition ${
                              usefulLinksAudience === "employee"
                                ? "border-redhalo-teal/80 text-redhalo-navy dark:text-redhalo-mint bg-white/90 dark:bg-[#1D1D1D] hover:bg-redhalo-mint/60 dark:hover:bg-redhalo-navy/40"
                                : "border-redhalo-orange/50 text-redhalo-navy dark:text-redhalo-warm bg-white/90 dark:bg-[#1D1D1D] hover:bg-redhalo-cream/80 dark:hover:bg-redhalo-navy-dark/50"
                            }`}
                          >
                            {link.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setUsefulLinksAudience((a) =>
                        a === "employee" ? "employer" : "employee"
                      )
                    }
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                      usefulLinksAudience === "employee"
                        ? "border-redhalo-red bg-redhalo-red text-white"
                        : "border-redhalo-orange bg-redhalo-orange text-white"
                    }`}
                    title="Toggle Employee / Employer links"
                  >
                    {usefulLinksAudience === "employee" ? "Employee" : "Employer"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedPanels((prev) => ({
                        ...prev,
                        usefulLinks: !prev.usefulLinks,
                      }))
                    }
                    className="flex-1 min-w-0 flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 dark:border-[#3C3C4C] border-l-[3px] border-l-redhalo-red bg-redhalo-mint/40 dark:bg-[#1D1D1D] hover:bg-redhalo-mint/70 dark:hover:bg-[#2a2a2a] transition"
                  >
                    <span className="text-sm font-medium text-redhalo-navy dark:text-gray-100 truncate">
                      Useful Links
                    </span>
                    {expandedPanels.usefulLinks ? (
                      <ChevronUp size={14} className="text-gray-500 dark:text-gray-300 shrink-0" />
                    ) : (
                      <ChevronDown size={14} className="text-gray-500 dark:text-gray-300 shrink-0" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Refresh button */}
          <div className="p-4">
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-blue-500 underline"
            >
              Refresh Company Data
            </button>
          </div>

          {/* Global Settings */}
          {/* <div className="p-4 border-t border-gray-200">
            <Link
              to="/admin/global-settings"
              onClick={() => setCurrentPage("Global Settings")}
              className="flex items-center space-x-4 w-full px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
            >
              <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                <Settings className="w-5 h-5" />
              </span>
              <span className="font-medium text-sm">Global Settings</span>
            </Link>
          </div> */}

          {/* Dark Mode Toggle */}
          {/* <div className="p-4 border-t border-gray-100">
          <button
            onClick={toggleDarkMode}
            className="flex items-center space-x-4 w-full px-4 py-3.5 text-gray-700 hover:text-gray-900 hover:bg-white rounded-xl transition-all duration-200 group"
          >
            <span className="text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </span>
            <span className="font-medium text-sm">
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </span>
          </button>
        </div> */}
        </aside>
      </div>
      {statusModal.open && (
        <RequestStatusDeskModal
          darkMode={darkMode}
          panelKey={statusModal.panelKey}
          panelTypeItems={panelTypeItemsByKey[statusModal.panelKey] || []}
          selectedType={selectedTypeByPanel[statusModal.panelKey] || ALL_DESK_TYPES}
          onSelectType={(typeLabel) => {
            setSelectedTypeByPanel((prev) => ({ ...prev, [statusModal.panelKey]: typeLabel }));
            setSelectedRequestId(null);
          }}
          selectedStatus={selectedStatusByPanel[statusModal.panelKey] || statusModal.status || "Pending"}
          onSelectStatus={(status) => {
            setStatusModal((prev) => ({ ...prev, status }));
            applyStatusSelection(status);
          }}
          onClose={closeStatusModal}
          onOpenForm={openStatusDeskForm}
          filteredRequests={getFilteredRequests()}
          selectedRequestId={selectedRequestId}
          onSelectRequest={setSelectedRequestId}
          onOpenStatusAction={openStatusActionModal}
          onOpenBulkStatusAction={openBulkStatusActionModal}
        />
      )}
      <QuickRequestFormModal
        darkMode={darkMode}
        isOpen={statusDeskShowForm}
        actionLabel={statusDeskContext?.actionLabel || ""}
        context={statusDeskContext}
        quickActionDraft={quickActionDraft}
        setQuickActionDraft={setQuickActionDraft}
        maxDateToday={maxDateToday}
        onClose={closeStatusDeskForm}
        onSubmitQuickAction={submitQuickAction}
        onSubmitRegularise={submitRegulariseQuickAction}
      />
      {statusActionModal.open && (
        <div className="fixed inset-0 z-[10025] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-xl dark:border-[#3C3C4C] dark:bg-[#121212]">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-[#3C3C4C]">
              <h3 className="text-sm font-semibold text-black dark:text-gray-100">
                {statusActionModal.nextStatus}{" "}
                {statusActionModal.requestIds.length > 1
                  ? `${statusActionModal.requestIds.length} Requests`
                  : "Request"}
              </h3>
              <button
                type="button"
                onClick={closeStatusActionModal}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-[#3C3C4C] dark:text-gray-200 dark:hover:bg-[#1a1a1a]"
                aria-label="Close reason popup"
              >
                <X size={14} />
              </button>
            </div>
            <div className="space-y-2 p-4">
              <label className="block text-xs font-medium text-black dark:text-gray-200">
                Remarks <span className="text-red-600">*</span>
                <textarea
                  rows={4}
                  maxLength={250}
                  value={statusActionModal.reason}
                  onChange={(e) =>
                    setStatusActionModal((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-black dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-100"
                  placeholder={`Enter remarks for ${statusActionModal.nextStatus.toLowerCase()}...`}
                />
              </label>
              <div className="text-right text-[11px] text-gray-500 dark:text-gray-400">
                {(statusActionModal.reason || "").length}/250
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeStatusActionModal}
                  className="rounded-md border border-gray-300 bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-200 dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-200 dark:hover:bg-[#2a2a2a]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    !(statusActionModal.reason || "").trim() ||
                    (statusActionModal.reason || "").trim().length > 250
                  }
                  onClick={confirmStatusAction}
                  className="rounded-md border border-redhalo-red bg-redhalo-red px-3 py-1.5 text-xs font-medium text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default Sidebar;
